import { ApiData, EntityID } from "./models";
import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";

/**
 * Get a specific entity instance by UUID
 * 
 * @param instance_uuid UUID of the instance to retrieve
 * @param user User ID for the request
 * @param command_dispatcher Command dispatcher instance
 * @returns The entity instance
 */
export async function getEntityInstance(
  instance_uuid: string,
  user: string,
  command_dispatcher: CommandDispatcher
): Promise<{ [key: string]: any }> {
  return command_dispatcher.dispatch(
    "get.entity.instance",
    {
      instance_uuid: instance_uuid,
      user_id: user
    }
  );
}

/**
 * Get multiple entity instances by their UUIDs
 * 
 * @param instances_ids List of UUIDs of the instances to retrieve
 * @param user User ID for the request
 * @param query_dispatcher Query dispatcher instance
 * @returns List of entity instances
 */
export async function getMultipleEntityInstances(
  instances_ids: string[],
  user: string,
  query_dispatcher: QueryDispatcher
): Promise<ApiData[]> {
  const res = await query_dispatcher.dispatch(
    "get.multiple.entity.instances",
    {
      instances_ids: instances_ids,
      user_id: user
    }
  );

  if (!res) {
    console.warn(`No instances found for IDs: ${instances_ids}`);
    return [];
  }

  // Convert res to ApiData
  const res_instances: ApiData[] = res.map((data: any) => {
    return {
      uuid: data.uuid,
      entity_type_id: data.entity_type_id,
      company_id: data.company_id,
      satisfies_schema: data.satisfies_schema,
      payload: data.payload,
      created: data.created,
      deleted: data.deleted,
      download_date: data.download_date
    };
  });

  console.info(`Retrieved ${res_instances.length} instances for IDs: ${instances_ids}`);
  return res_instances;
}

/**
 * Get all entities of a specific type
 * 
 * @param entity_type_id The ID of the entity type to retrieve
 * @param user User ID for the request
 * @param company_id Company ID for the request
 * @param query_dispatcher Query dispatcher instance
 * @returns List of entities of the specified type
 */
export async function getEntityByType(
  entity_type_id: EntityID,
  user: string,
  company_id: number,
  query_dispatcher: QueryDispatcher
): Promise<{ [key: string]: any }[]> {
  const result = await query_dispatcher.dispatch(
    "get.instances.by.type",
    {
      user_id: user,
      entity_type_id: entity_type_id,
      company_id: company_id
    }
  );
  if (Array.isArray(result)) {
    return result;
  }
  if (!result) {
    return [];
  }
  return [result];
}